import { useCallback, useEffect, useState } from 'react';

// One-time check for native CSS anchor positioning support
const hasNativeCSSAnchorSupport =
    'anchorName' in document.documentElement.style;

/**
 * Custom hook for CSS anchor positioning with Floating UI fallback.
 *
 * Uses native CSS anchor positioning when supported, falls back to Floating UI
 * for browsers without support. Always uses automatic placement to choose the
 * best position with the most available space.
 *
 * Note: This hook requires a custom anchor name because the HTML `anchor`
 * attribute is not yet widely supported. The anchor name must be unique
 * across the document and will be set via CSS properties.
 *
 * @param anchorName - The anchor name to set (e.g., '--my-anchor') - must be unique
 * @returns Object with setAnchorElement and setPositionedElement
 *
 * @example
 * ```tsx
 * const { setAnchorElement, setPositionedElement } = useCSSAnchorPosition('--my-anchor');
 *
 * return (
 *   <>
 *     <button ref={setAnchorElement}>
 *       Trigger element
 *     </button>
 *     <div ref={setPositionedElement} style={{ position: 'absolute' }}>
 *       Positioned element
 *     </div>
 *   </>
 * );
 * ```
 */
export function useCSSAnchorPosition<
    TAnchor extends HTMLElement = HTMLElement,
    TPositioned extends HTMLElement = HTMLElement,
>(anchorName: string) {
    const [anchorElement, setAnchorElementState] = useState<TAnchor | null>(
        null,
    );
    const [positionedElement, setPositionedElementState] =
        useState<TPositioned | null>(null);
    const [cleanup, setCleanup] = useState<(() => void) | null>(null);

    // Ref callbacks that trigger re-renders when elements are available
    const setAnchorElement = useCallback((node: TAnchor | null) => {
        setAnchorElementState(node);
    }, []);

    const setPositionedElement = useCallback((node: TPositioned | null) => {
        setPositionedElementState(node);
    }, []);

    // Set up CSS anchor positioning or Floating UI fallback
    useEffect(() => {
        if (!anchorName || !anchorElement || !positionedElement) {
            return;
        }

        // Sanitize the anchor name to remove invalid characters
        const sanitizedAnchorName = anchorName.replace(/[^a-zA-Z0-9\-_]/g, '-');

        if (hasNativeCSSAnchorSupport) {
            // Use native CSS anchor positioning
            anchorElement.style.setProperty('anchor-name', sanitizedAnchorName);

            positionedElement.style.setProperty(
                'position-anchor',
                sanitizedAnchorName,
            );
        } else {
            // Use Floating UI fallback with CSS-aware positioning
            const setupFallback = async () => {
                // Lazy load the fallback module
                const { setupFloatingUIFallback } = await import('./fallback');

                const cleanupFunction = await setupFloatingUIFallback(
                    anchorElement,
                    positionedElement,
                );

                setCleanup(cleanupFunction);
            };

            void setupFallback();
        }

        // Cleanup function
        return () => {
            if (cleanup) {
                cleanup();
                setCleanup(null);
            }
        };
    }, [anchorName, anchorElement, positionedElement]);

    return {
        setAnchorElement,
        setPositionedElement,
        hasNativeSupport: hasNativeCSSAnchorSupport,
    };
}
